import { Resource } from './resource';
import { DurationUnit } from './durations';
import { Sensor } from './api';

export enum TriggerDataCaptureStep {
  INITIAL = 'INITIAL',
  REGULAR = 'REGULAR',
  DENSE = 'DENSE',
}

export enum TriggerDataCaptureLength {
  SHORT = 'SHORT',
  LONG = 'LONG',
  CUSTOM = 'CUSTOM',
}

type CaptureLength =
  | {
      captureLength: Exclude<
        TriggerDataCaptureLength,
        TriggerDataCaptureLength.CUSTOM
      >;
    }
  | {
      captureLength: TriggerDataCaptureLength.CUSTOM;
      customCaptureLengthInSeconds: string;
    };

export type RegularDataCaptureState = {
  open: boolean;
  formDisabled: boolean;
  step: TriggerDataCaptureStep.REGULAR;
  motion: boolean;
  motionDetectionTriggerLine: boolean;
} & CaptureLength;

export type DenseDataCaptureState = {
  open: boolean;
  formDisabled: boolean;
  step: TriggerDataCaptureStep.DENSE;

  numberOfCaptures: string;
  maxCaptureDuration: string;
  maxCaptureDurationUnit: DurationUnit;

  motion: boolean;
  motionDetectionTriggerLine: boolean;
  advancedControlsVisible: boolean;
} & CaptureLength;

export type TriggerDataCaptureState =
  | {
      open: boolean;
      formDisabled: boolean;
      step: TriggerDataCaptureStep.INITIAL;
    }
  | RegularDataCaptureState
  | DenseDataCaptureState;

export enum TriggerDataCaptureActionTypes {
  TRIGGER_DATA_CAPTURE_OPEN = 'TRIGGER_DATA_CAPTURE_OPEN',
  TRIGGER_DATA_CAPTURE_CLOSE = 'TRIGGER_DATA_CAPTURE_CLOSE',

  TRIGGER_DATA_CAPTURE_SUBMIT = 'TRIGGER_DATA_CAPTURE_SUBMIT',
  TRIGGER_DATA_CAPTURE_LOADING = 'TRIGGER_DATA_CAPTURE_LOADING',
  TRIGGER_DATA_CAPTURE_COMPLETE = 'TRIGGER_DATA_CAPTURE_COMPLETE',
  TRIGGER_DATA_CAPTURE_ERROR = 'TRIGGER_DATA_CAPTURE_ERROR',

  TRIGGER_DATA_CAPTURE_CHANGE_STEP = 'TRIGGER_DATA_CAPTURE_CHANGE_STEP',
  TRIGGER_DATA_CAPTURE_SHOW_ADVANCED_CONTROLS = 'TRIGGER_DATA_CAPTURE_SHOW_ADVANCED_CONTROLS',

  TRIGGER_DATA_CAPTURE_CHANGE_MOTION = 'TRIGGER_DATA_CAPTURE_CHANGE_MOTION',
  TRIGGER_DATA_CAPTURE_CHANGE_MOTION_DETECTION_TRIGGER_LINE = 'TRIGGER_DATA_CAPTURE_CHANGE_MOTION_DETECTION_TRIGGER_LINE',
  TRIGGER_DATA_CAPTURE_CHANGE_CAPTURE_LENGTH = 'TRIGGER_DATA_CAPTURE_CHANGE_CAPTURE_LENGTH',
  TRIGGER_DATA_CAPTURE_CHANGE_CUSTOM_CAPTURE_LENGTH = 'TRIGGER_DATA_CAPTURE_CHANGE_CUSTOM_CAPTURE_LENGTH',
  TRIGGER_DATA_CAPTURE_CHANGE_MAX_CAPTURE_DURATION = 'TRIGGER_DATA_CAPTURE_CHANGE_MAX_CAPTURE_DURATION',
  TRIGGER_DATA_CAPTURE_CHANGE_MAX_CAPTURE_DURATION_UNIT = 'TRIGGER_DATA_CAPTURE_CHANGE_MAX_CAPTURE_DURATION_UNIT',
  TRIGGER_DATA_CAPTURE_CHANGE_NUM_CAPTURES = 'TRIGGER_DATA_CAPTURE_CHANGE_NUM_CAPTURES',
}

export type TriggerDataCaptureAction =
  | {
      type: TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_OPEN;
      openIntoRegularStep: boolean;
    }
  | { type: TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_CLOSE }
  | {
      type: TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_SUBMIT;
      serialNumbers: Array<Sensor['serial_number']>;
      onComplete?: () => void;
    }
  | { type: TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_LOADING }
  | { type: TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_COMPLETE }
  | { type: TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_ERROR }
  | {
      type: TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_CHANGE_STEP;
      step: TriggerDataCaptureStep;
    }
  | {
      type: TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_SHOW_ADVANCED_CONTROLS;
    }
  | {
      type: TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_CHANGE_MOTION;
      checked: boolean;
    }
  | {
      type: TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_CHANGE_MOTION_DETECTION_TRIGGER_LINE;
      checked: boolean;
    }
  | {
      type: TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_CHANGE_CAPTURE_LENGTH;
      value: TriggerDataCaptureLength;
    }
  | {
      type: TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_CHANGE_CUSTOM_CAPTURE_LENGTH;
      value: string;
    }
  | {
      type: TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_CHANGE_MAX_CAPTURE_DURATION;
      value: string;
    }
  | {
      type: TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_CHANGE_MAX_CAPTURE_DURATION_UNIT;
      value: DurationUnit;
    }
  | {
      type: TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_CHANGE_NUM_CAPTURES;
      value: string;
    };
